// import isoCodeToCountry from '~/modules/utils/iso-code-to-country';
// import eurovisionIcon from '~/routes/sing-a-song/song-selection/components/song-card/eurovision-icon.svg';
import { ReactElement, ReactNode, useMemo } from 'react';

import { SongPreview } from '~/interfaces';
import { Icon } from '~/modules/elements/akui/icon';
import Typography from '~/modules/elements/akui/primitives/typography';
import { ClosableTooltip } from '~/modules/elements/tooltip';
import useRemoteMicServerStatus from '~/modules/remote-mic/hooks/use-remote-mic-server-status';
import { useLanguageList } from '~/modules/songs/hooks/use-language-list';
import useRemoteMicSongList from '~/modules/songs/hooks/use-remote-mic-song-list';
import { useSetlist } from '~/modules/songs/hooks/use-setlist';
import { FeatureFlags } from '~/modules/utils/feature-flags';
import useFeatureFlag from '~/modules/utils/use-feature-flag';
import { eurovisionPlaylist } from '~/routes/sing-a-song/song-selection/hooks/use-playlists-eurovision';
import { SongGroup } from '~/routes/sing-a-song/song-selection/hooks/use-song-list';
import { AppliedFilters } from '~/routes/sing-a-song/song-selection/hooks/use-song-list-filter';

export interface PlaylistEntry {
  name: string;
  display?: ReactNode;
  /**
   * Plain-text label for the remote mic's mirrored playlist picker. `display` can't be sent (it's
   * JSX — logos, tooltips), so an entry whose `name` isn't presentable on its own supplies one.
   */
  remoteLabel?: string;
  hideNew?: boolean;
  Wrapper?: (props: { children: ReactElement; focused: boolean; active: boolean }) => ReactNode;
  filters: AppliedFilters;
  groupData?: (song: SongPreview) => Pick<SongGroup, 'name' | 'displayShort' | 'displayLong' | 'remoteLabel'>;
  postGrouping?: (groups: SongGroup[]) => SongGroup[];
  sortingFn?: (a: SongPreview, b: SongPreview) => number;
  footerComponent?: ReactNode;
}

export interface PersonalRecommendations {
  songIds: string[];
  forProfiles: Array<{ id: string; name: string }>;
}

/**
 * The playlists that do not count as a filter being on. `All` narrows nothing, and `Selection` and
 * `For you` are whichever view the screen opened on rather than something the room chose - marking
 * them would put a dot on the filter icon before anybody had touched it.
 */
const UNMARKED_PLAYLISTS = ['All', 'Selection', 'For you'];

export const isPlaylistNarrowing = (playlist: string | null) =>
  playlist !== null && !UNMARKED_PLAYLISTS.includes(playlist);

/** Module-level, so the default is the same object every render and cannot invalidate the memo. */
const NO_RECOMMENDATIONS: PersonalRecommendations = { songIds: [], forProfiles: [] };

export const usePlaylists = (
  songs: SongPreview[],
  recommended: string[],
  isLoading: boolean,
  personal: PersonalRecommendations = NO_RECOMMENDATIONS,
): PlaylistEntry[] => {
  const { isSetlistInPlace } = useSetlist();
  const isSpecialThemeEnabled = useFeatureFlag(FeatureFlags.Eurovision);
  const songLanguages = useLanguageList(songs);
  const remoteSongList = useRemoteMicSongList();
  const { connected } = useRemoteMicServerStatus();

  return useMemo<PlaylistEntry[]>(() => {
    if (isLoading) return [];

    const selection: PlaylistEntry = {
      name: 'Selection',
      Wrapper: ({ children, active, focused }) => (
        <ClosableTooltip
          {...(active && focused ? { open: true } : {})}
          oneTime={false}
          timeoutMs={15_000}
          dismissKey="selection-playlist"
          place={'right'}
          title={
            <>
              A combination of songs you might like and popular with other players.
              <br />
              <br /> <strong>Search</strong> is performed across all the songs.
            </>
          }>
          {children}
        </ClosableTooltip>
      ),
      display: (
        <span>
          <strong>★</strong>&nbsp;Selection
        </span>
      ),
      remoteLabel: '★ Selection',
      filters: {
        specificSongs: recommended,
      },
    };

    // Only when a profile on a player slot has a history to draw on. With nothing personal to
    // say, the entry would just be a worse copy of Selection.
    const personalPlaylist: PlaylistEntry | null =
      personal.songIds.length > 0
        ? {
            name: 'For you',
            display: (
              <span>
                <strong>♥</strong>&nbsp;
                {personal.forProfiles.length === 1 ? `For ${personal.forProfiles[0].name}` : 'For you'}
              </span>
            ),
            remoteLabel: personal.forProfiles.length === 1 ? `For ${personal.forProfiles[0].name}` : 'For you',
            filters: { specificSongs: personal.songIds },
            // The ranking is the point, so keep the order the recommendations came in.
            sortingFn: (a, b) => personal.songIds.indexOf(a.id) - personal.songIds.indexOf(b.id),
          }
        : null;

    const all: PlaylistEntry = { name: 'All', filters: {} };
    const playlists: Array<PlaylistEntry | null> = [
      !isSetlistInPlace ? personalPlaylist : null,
      !isSetlistInPlace ? selection : null, // makes "all" playlist the first one if a setlist is loaded
      all,
      !isSetlistInPlace && isSpecialThemeEnabled ? eurovisionPlaylist : null,
      // {
      //   name: 'Halloween',
      //   display: (
      //     <>
      //       <span style={{ color: colorSets.halloweenViolet.text }}>Hallo</span>
      //       <span style={{ color: colorSets.halloweenOrange.text }}>ween</span> 🎃
      //     </>
      //   ),
      //   filters: { edition: 'halloween' },
      // },
      songLanguages[0]
        ? ({
            name: songLanguages[0].name,
            filters: { language: songLanguages[0].name },
          } as PlaylistEntry)
        : null,
      songLanguages[1]
        ? {
            name: songLanguages[1].name,
            filters: { language: songLanguages[1].name },
          }
        : null,
      { name: 'Duets', filters: { duet: true } },
      !isSetlistInPlace
        ? {
            name: 'New',
            filters: { recentlyUpdated: true },
          }
        : null,
      connected.length > 0
        ? {
            name: 'remote-mics',
            display: <>From &nbsp;Phones</>,
            remoteLabel: 'From Phones',
            filters: { specificSongs: remoteSongList, skipExcludedLanguages: true },
            footerComponent: (
              <div data-test="remote-mic-playlist-tip" className="flex justify-center">
                <Typography className="text-xl">
                  Add songs on your <strong>phone</strong> using{' '}
                  <strong>
                    <Icon icon="ic:baseline-list" className="text-[1em]!" /> Song List
                  </strong>{' '}
                  tab on the bottom of the screen
                </Typography>
              </div>
            ),
          }
        : null,
      // Every other language lives in the filter panel's Language category now, which reaches all of
      // them rather than the two that fit here plus a picker sheet.
    ];

    return playlists.filter((playlist): playlist is PlaylistEntry => playlist !== null);
  }, [
    songLanguages,
    isLoading,
    recommended,
    remoteSongList,
    connected.length,
    isSetlistInPlace,
    isSpecialThemeEnabled,
    personal.songIds,
    personal.forProfiles,
  ]);
};
